import { useEffect, useRef } from 'react';
import { useIsMobile } from '@/core/hooks/useMediaQuery';
import { AppLink } from '@/shared/components/AppLink';

interface SquareMenuSectionProps {
  data: Record<string, unknown> & {
    items?: Array<{
      title: string;
      imageUrl: string;
      link: string;
      /** When true, navigate with full page refresh; when false, use React Router SPA nav. */
      shouldRefresh?: boolean;
    }>;
    layoutRows?: number;
    title?: string[];
  };
  sectionKey: string;
  eventLabel?: string;
  priority?: number;
}

export default function SquareMenuSection({ 
  data, 
  sectionKey: _sectionKey, 
  eventLabel: _eventLabel, 
  priority: _priority 
}: SquareMenuSectionProps) {
  const items = data.items || [];
  const layoutRows = data.layoutRows || 2;
  const isMobile = useIsMobile();

  const columns = Math.ceil(items.length / layoutRows);

  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateGridColumns = () => {
      if (!gridRef.current) return;
      
      if (!isMobile) {
        // Desktop: auto-adjusting grid
        gridRef.current.style.gridTemplateColumns = `repeat(${columns}, minmax(0, 1fr))`;
        gridRef.current.style.width = 'auto';
      } else {
        // Mobile: fixed width for 3.5 items visible
        // gap-3 = 0.75rem = 12px, pl-4 pr-4 = 1rem each = 32px total
        // For 3.5 items, we have 2.5 gaps (between 3.5 items) = 2.5 * 12px = 30px
        // Item width = (100vw - 30px - 32px) / 3.5 = (100vw - 62px) / 3.5
        const itemWidth = 'calc((100vw - 62px) / 3.5)';
        gridRef.current.style.gridTemplateColumns = `repeat(${columns}, ${itemWidth})`;
        gridRef.current.style.width = 'max-content';
      }
    };

    updateGridColumns();
  }, [columns, isMobile]);

  return (
    <section
      className="overflow-hidden py-[16px] px-[10px] md:py-12"
    >
      <div className="overflow-x-auto md:overflow-x-visible -mx-4 px-4 pb-4 md:mx-0 md:px-0">
        <div 
          ref={gridRef}
          className="menu-wrap grid gap-3 md:gap-6 md:container-fluid"
          style={{
            gridTemplateColumns: `repeat(${columns}, 20vw)`,
            width: 'max-content',
          }}
        >
        {items.map((item, index) => (
          <div
            key={`${item.title}-${item.link}-${index}`}
            className={[
              'menu-item md:w-auto transform-gpu',
              'transition-transform duration-[1500ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
              'motion-reduce:transition-none motion-reduce:transform-none',
              // Triggered by SectionRenderer's data-inview="true" (desktop only).
              index % 2 === 0
                ? 'translate-x-0 md:group-data-[inview=true]:translate-x-[calc(100%+25px)]'
                : 'translate-x-0 md:group-data-[inview=true]:translate-x-[calc(-100%-25px)]',
            ].join(' ')}
            style={isMobile ? { width: 'calc((100vw - 62px) / 3.5)' } : {}}
          >
            <AppLink
              to={item.link}
              shouldRefresh={item.shouldRefresh}
              eventName="Category Card Clicked"
              eventProperties={{
                'Category Name': item.title,
                Position: index + 1,
              }}
              className="flex flex-col items-center gap-1 hover:opacity-80 transition-opacity"
            >
              <div 
                className={`image-wrap overflow-hidden relative text-center mb-[5px] md:mb-0 transition-all duration-500 ease-in-out
                  ${isMobile 
                    ? 'w-full aspect-square border border-[#F5F5F5] rounded-md bg-[#FCFCFB] shadow-[2px_2px_5px_rgba(221,197,197,0.25)] p-2.5' 
                    : 'w-full aspect-square rounded-[24px] bg-[#FFF8F9] shadow-[2px_2px_5px_0px_rgba(221,197,197,0.25)] border-[0.3px] border-[rgba(221,197,197,0.5)]'
                  }`}
              >
                <img
                  className="max-w-full max-h-full absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                  src={item.imageUrl}
                  alt={item.title}
                  width={212}
                  height={212}
                  loading="lazy"
                />
              </div>
              <h2 
                className={`title whitespace-nowrap md:whitespace-normal font-sans
                  ${isMobile 
                    ? 'text-xs text-[#9A777D] text-center font-normal p-0 m-0 border-none' 
                    : 'text-center font-medium text-sm md:text-[14px] md:text-[#BFB9BD] md:leading-4 md:mt-[10px] md:mb-0 md:mx-0'
                  }`}
              >
                {item.title}
              </h2>
            </AppLink>
          </div>
        ))}
        </div>
      </div>
    </section>
  );
}

